<template>
  <div v-if="loading || consentUrl" class="consent-url">
    <div class="consent-url__label">{{ $t('settings.entraConsentUrlLabel') }}</div>

    <div v-if="loading" class="consent-url__loading">
      <i class="el-icon-loading"></i>
      {{ $t('settings.entraConsentUrlLoading') }}
    </div>

    <a
      v-else
      :href="consentUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="consent-url__link">
      <i class="el-icon-link consent-url__icon"></i>
      <span class="consent-url__text">{{ consentUrl }}</span>
      <i class="el-icon-top-right consent-url__open"></i>
    </a>
  </div>
</template>

<script>
import {entraConnectionService} from '@/services/EntraConnectionService.class';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default {
  name: 'EntraConsentUrl',
  props: {
    /**
     * Whenever it becomes a valid UUID,
     * the matching admin consent URL is fetched and shown as a link.
     */
    tenantId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      consentUrl: null,
      loading: false,
    };
  },
  watch: {
    tenantId: {
      immediate: true,
      handler() {
        this.loadConsentUrl();
      },
    },
  },
  methods: {
    isValidTenantId(value) {
      return UUID_PATTERN.test((value || '').trim());
    },
    async loadConsentUrl() {
      const tenantId = (this.tenantId || '').trim();
      this.consentUrl = null;

      if (!this.isValidTenantId(tenantId)) {
        return;
      }

      this.loading = true;
      try {
        const {data} = await entraConnectionService.getAdminConsentUrl(tenantId, window.location.origin);
        if ((this.tenantId || '').trim() !== tenantId) {
          return;
        }
        this.consentUrl = data.adminConsentUrl;
      } catch (e) {
        if ((this.tenantId || '').trim() === tenantId) {
          this.consentUrl = null;
        }
      } finally {
        if ((this.tenantId || '').trim() === tenantId) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.consent-url {
  margin-bottom: 20px;

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.4;
    padding-bottom: 6px;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 42px;
    padding: 0 12px;
    border: 1px dashed var(--border);
    border-radius: 8px;
    background: #fafbff;
    font-size: 13px;
    color: var(--text-secondary);
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 8px 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: #f5f8ff;
    color: var(--primary);
    font-size: 13px;
    text-decoration: none;
    word-break: break-all;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
      border-color: var(--primary);
      background: #eef4ff;
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    line-height: 1.4;
  }

  &__open {
    flex-shrink: 0;
    font-size: 15px;
  }
}
</style>
